import { ApiErrorCode, ApiResponse, withApiErrorHandler } from '@/modules/common/api/errors';
import { axiosInstance } from '@/utils/axios';

import { mapReport, mapReportData } from '../mappers/report.mapper';
import { DateFilter, Report, ReportData } from '../types';
import { ReportColumnsResponse, ReportDataResponse, ReportResponse } from '../types/dto';

export const getReports = async (): Promise<Report[]> => {
  const endpoint = '/api/reports';
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORTS_LOAD_FAILED, () =>
    axiosInstance.get<ApiResponse<ReportResponse[]>>(endpoint)
  );
  return data.map(mapReport);
};

export const getReport = async (id: string): Promise<Report> => {
  const endpoint = `/api/reports/${id}`;
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_LOAD_FAILED, () =>
    axiosInstance.get<ApiResponse<ReportResponse>>(endpoint)
  );
  return mapReport(data);
};

export const createReport = async (name: string, scope: 'ORG' | 'PRIVATE'): Promise<Report> => {
  const endpoint = '/api/reports';
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_CREATE_FAILED, () =>
    axiosInstance.post<ApiResponse<ReportResponse>>(endpoint, { name, scope })
  );
  return mapReport(data);
};

export const updateReport = async (id: string, name: string): Promise<Report> => {
  const endpoint = `/api/reports/${id}`;
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_UPDATE_FAILED, () =>
    axiosInstance.patch<ApiResponse<ReportResponse>>(endpoint, { name })
  );
  return mapReport(data);
};

export const deleteReport = async (id: string): Promise<void> => {
  const endpoint = `/api/reports/${id}`;
  await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_DELETE_FAILED, () =>
    axiosInstance.delete<ApiResponse<Record<string, never>>>(endpoint)
  );
};

export const getAvailableColumns = async (type: string): Promise<ReportColumnsResponse> => {
  const endpoint = '/api/reports/columns';
  return withApiErrorHandler(endpoint, ApiErrorCode.REPORT_DATA_LOAD_FAILED, () =>
    axiosInstance.get<ApiResponse<ReportColumnsResponse>>(endpoint, { params: { type } })
  );
};

export const getReportData = async (body: {
  reportType: string;
  columns: string[];
  groupBy: string[];
  filters?: Record<string, unknown>;
}): Promise<ReportData> => {
  const endpoint = '/api/reports/data';
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_DATA_LOAD_FAILED, () =>
    axiosInstance.post<ApiResponse<ReportDataResponse>>(endpoint, body)
  );
  return mapReportData(data);
};

export const getElementData = async (
  reportId: string,
  elementId: string,
  filters: DateFilter,
  pagination?: { cursor?: number; limit?: number }
): Promise<ReportData> => {
  const endpoint = `/api/reports/${reportId}/elements/${elementId}/data`;
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_DATA_LOAD_FAILED, () =>
    axiosInstance.post<ApiResponse<ReportDataResponse>>(endpoint, { ...filters, ...pagination })
  );
  return mapReportData(data);
};

export const createElement = async (reportId: string, body: CreateElementBody): Promise<Report> => {
  const endpoint = `/api/reports/${reportId}/elements`;
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_ELEMENT_CREATE_FAILED, () =>
    axiosInstance.post<ApiResponse<ReportResponse>>(endpoint, body)
  );
  return mapReport(data);
};

export interface CreateElementBody {
  type: string;
  position: { x: number; y: number; width: number; height: number };
  config: Record<string, unknown>;
  reportType?: string;
  columns?: string[];
  groupBy?: string[];
  pivotDimension?: string | null;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc' | null;
  customMeasures?: unknown[];
  localFilters?: unknown[];
}

export interface UpdateElementBody {
  type?: string;
  position?: { x: number; y: number; width: number; height: number };
  config?: Record<string, unknown>;
  reportType?: string;
  columns?: string[];
  groupBy?: string[];
  pivotDimension?: string | null;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc' | null;
  customMeasures?: unknown[];
  localFilters?: unknown[];
}

export interface ElementUpdateBody {
  elementId: string;
  changes: UpdateElementBody;
}

export interface ApplyElementChangesBody {
  deleteElementIds: string[];
  updates: ElementUpdateBody[];
  creates: CreateElementBody[];
}

export const updateElement = async (
  reportId: string,
  elementId: string,
  body: UpdateElementBody
): Promise<Report> => {
  const endpoint = `/api/reports/${reportId}/elements/${elementId}`;
  const data = await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_ELEMENT_UPDATE_FAILED, () =>
    axiosInstance.patch<ApiResponse<ReportResponse>>(endpoint, body)
  );
  return mapReport(data);
};

export const deleteElement = async (reportId: string, elementId: string): Promise<void> => {
  const endpoint = `/api/reports/${reportId}/elements/${elementId}`;
  await withApiErrorHandler(endpoint, ApiErrorCode.REPORT_ELEMENT_DELETE_FAILED, () =>
    axiosInstance.delete<ApiResponse<Record<string, never>>>(endpoint)
  );
};

/** Deletes, then updates, then creates — all-or-nothing on the backend. */
export const applyElementChanges = async (
  reportId: string,
  body: ApplyElementChangesBody
): Promise<Report> => {
  const endpoint = `/api/reports/${reportId}/elements/batch`;
  const data = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.REPORT_ELEMENT_CHANGES_APPLY_FAILED,
    () => axiosInstance.post<ApiResponse<ReportResponse>>(endpoint, body)
  );
  return mapReport(data);
};
